import { useTranslation } from "i18next-vue";
import { computed, ref } from "vue";
import { getLocaleName } from "#/utils/i18next";

// NOTE: тексты ошибок ниже захардкожены на русском (мимо i18n) — намеренно сохранено как было.
// Локализация — отдельной задачей (в useModifiersCore/store валидация шла через i18next.t).

const sumGroupModifiersAmount = (modifiers: any[] = []) => modifiers.reduce((sum: number, mod: any) => sum + (mod.amount || 0), 0);

export function useComboValidation(comboList: any, comboSelections: any) {
	const { t, i18next } = useTranslation();
	const firstErrorGroupId = ref<string | null>(null);
	const firstErrorProductId = ref<string | null>(null);
	const validationErrorMessage = ref<string>("");

	const toArray = (value: any) => [value].flat().filter(Boolean);

	const setFirstError = (groupId: string, productId: string | null, message: string) => {
		if (!firstErrorGroupId.value) {
			firstErrorGroupId.value = groupId;
			firstErrorProductId.value = productId;
		}
		if (!validationErrorMessage.value) {
			validationErrorMessage.value = message;
		}
	};

	const validateRequiredProduct = (comboGroup: any, selection: any) => {
		if (comboGroup.required && !selection?.product) {
			setFirstError(comboGroup.id, null, t(`combo.validation.required-product`, { name: getLocaleName(comboGroup, i18next.language) }));
			return false;
		}
		return true;
	};

	const validateSingleModifiers = (comboGroup: any, product: any, selection: any) => {
		const selectedSingleMods: any[] = selection.modifiers || [];
		const totalSelected = sumGroupModifiersAmount(selectedSingleMods);
		const productName = getLocaleName(product, i18next.language);

		if (product.modifiers?.length) {
			const minTotal = toArray(product.modifiers).reduce((sum: number, mod: any) => sum + (mod.min_amount || 0), 0);
			if (minTotal > 0 && totalSelected < minTotal) {
				setFirstError(comboGroup.id, product.id, t(`combo.validation.required-modifiers`, { name: productName }));
				return false;
			}
		}

		for (const mod of toArray(product.modifiers)) {
			const selectedMod = selectedSingleMods.find((m: any) => m.id === mod.id);
			const selectedAmount = selectedMod?.amount ?? 0;
			const modifierName = getLocaleName(mod, i18next.language);

			if (typeof mod.min_amount === "number" && mod.min_amount > 0) {
				if (!selectedMod || selectedAmount < mod.min_amount) {
					setFirstError(
						comboGroup.id,
						product.id,
						t(`combo.validation.required-modifiers-min`, { name: productName, modifier: modifierName, amount: mod.min_amount }),
					);
					return false;
				}
			}

			if (typeof mod.max_amount === "number" && mod.max_amount > 0 && selectedAmount > mod.max_amount) {
				setFirstError(
					comboGroup.id,
					product.id,
					t(`combo.validation.required-modifiers-max`, { name: productName, modifier: modifierName, amount: mod.max_amount }),
				);
				return false;
			}
		}
		return true;
	};

	const validateGroupModifiers = (comboGroup: any, product: any, selection: any) => {
		const selectedGroupMods: any[] = selection.groupModifiers || [];
		const productName = getLocaleName(product, i18next.language);

		for (const group of toArray(product.group_modifiers)) {
			const selGroup = selectedGroupMods.find((selectedGroup: any) => (selectedGroup.id || selectedGroup.group) === group.id);
			const groupName = getLocaleName(group, i18next.language);
			const totalAmountInGroup = sumGroupModifiersAmount(selGroup?.modifiers);

			if (group.required && totalAmountInGroup === 0) {
				setFirstError(comboGroup.id, product.id, t(`combo.validation.required-modifiers-group`, { name: productName, group: groupName }));
				return false;
			}

			if (typeof group.min_amount === "number" && group.min_amount > 0) {
				if (totalAmountInGroup < group.min_amount) {
					setFirstError(
						comboGroup.id,
						product.id,
						t(`combo.validation.required-modifiers-group-min`, { name: productName, group: groupName, amount: group.min_amount }),
					);
					return false;
				}
			}

			if (typeof group.max_amount === "number" && group.max_amount > 0) {
				if (totalAmountInGroup > group.max_amount) {
					setFirstError(
						comboGroup.id,
						product.id,
						t(`combo.validation.required-modifiers-group-max`, { name: productName, group: groupName, amount: group.max_amount }),
					);
					return false;
				}
			}

			for (const mod of toArray(group.group_modifiers)) {
				const selMod = selGroup?.modifiers?.find((m: any) => m.id === mod.id);
				const selectedAmount = selMod?.amount ?? 0;
				const modifierName = getLocaleName(mod, i18next.language);

				if (typeof mod.min_amount === "number" && mod.min_amount > 0) {
					if (!selMod || selectedAmount < mod.min_amount) {
						setFirstError(
							comboGroup.id,
							product.id,
							t(`combo.validation.required-modifiers-group-modifier-min`, {
								name: productName,
								group: groupName,
								modifier: modifierName,
								amount: mod.min_amount,
							}),
						);
						return false;
					}
				}

				if (typeof mod.max_amount === "number" && mod.max_amount > 0 && selectedAmount > mod.max_amount) {
					setFirstError(
						comboGroup.id,
						product.id,
						t(`combo.validation.required-modifiers-group-modifier-max`, {
							name: productName,
							group: groupName,
							modifier: modifierName,
							amount: mod.max_amount,
						}),
					);
					return false;
				}
			}
		}
		return true;
	};

	const validateComboSelection = (): boolean => {
		firstErrorGroupId.value = null;
		firstErrorProductId.value = null;
		validationErrorMessage.value = "";

		for (const comboGroup of comboList.value as any[]) {
			const selection = comboSelections.value[comboGroup.id];

			if (!validateRequiredProduct(comboGroup, selection)) return false;
			if (!selection?.product) continue;

			const product = selection.product;

			if (product.modifiers?.length && !validateSingleModifiers(comboGroup, product, selection)) return false;

			if (product.group_modifiers?.length && !validateGroupModifiers(comboGroup, product, selection)) return false;
		}

		return true;
	};

	const hasErrors = computed(() => !!validationErrorMessage.value);

	return {
		firstErrorGroupId,
		firstErrorProductId,
		validationErrorMessage,
		validateComboSelection,
		hasErrors,
	};
}
